import React, { useEffect, useState } from "react";
import { Table, Descriptions } from "antd";
import { Content } from "antd/es/layout/layout";
import { toast } from "react-toastify";
import { getDolaresDoPeriodo, request } from "../../functions/request";

function formatDate(dateValue) {
  return new Date(dateValue).toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo",
  });
}

// Mesma lógica de busca do CorrecaoRelatorio.js (inclusive a expansão de
// "TODOS" em uma request por UF), só que devolve as linhas cruas em vez de
// já montar uma tabela, pra dar pra juntar com as outras cotações depois.
async function fetchLinhasCotacao(query, allUfs) {
  const params = new URLSearchParams(query);
  const data_start = params.get("data_start");
  const data_final = params.get("data_final");
  const ordem = params.get("ordem");
  const uf = params.get("uf") ?? "";

  let path = `/correcoes/getcorrecoes`;
  if (data_start && data_final) {
    path += `?data_start=${data_start}&data_final=${data_final}`;
  }
  if (ordem) {
    path += `&ordem=${ordem}`;
  }

  if (uf.toUpperCase() === "TODOS") {
    const ufsPromises = (allUfs || []).map(async (ufItem) => {
      const response = await request(`${path}&uf=${ufItem.uf}`, {
        method: "GET",
      });
      return response[0] || [];
    });
    const resultados = await Promise.all(ufsPromises);
    return resultados.flat();
  }

  const finalPath = uf ? `${path}&uf=${uf}` : path;
  const response = await request(finalPath, { method: "GET" });
  return response[0] || [];
}

/**
 * Junta várias cotações (já buscadas por UF+Data) numa única tabela, uma
 * linha por UF+Data, com um par de colunas Valor/Dólar pra cada cotação
 * selecionada — em vez de uma tabela separada por cotação.
 */
export default function CorrecaoRelatorioMesclado({ queries, allUfs, onDados }) {
  const [dadosTabela, setDadosTabela] = useState([]);
  const [colunas, setColunas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelado = false;

    async function montar() {
      if (!queries || queries.length === 0) return;
      setIsLoading(true);
      try {
        const porCotacao = await Promise.all(
          queries.map(async ({ id, query, nome }) => {
            const linhas = await fetchLinhasCotacao(query, allUfs);
            // Dólar do período numa requisição só (antes: uma por linha).
            const dolarDoDia = await getDolaresDoPeriodo(linhas.map((l) => l.campodt));
            const comDolar = await Promise.all(
              linhas.map(async (linha) => {
                const dolarRate = dolarDoDia(linha.campodt);
                return {
                  campouf: linha.campouf,
                  campodt: linha.campodt,
                  valor: linha.campo,
                  valorDolar: dolarRate
                    ? Number(linha.campo / dolarRate).toFixed(2)
                    : null,
                  dolarRate: dolarRate || null,
                };
              })
            );
            return { id, nome, linhas: comDolar };
          })
        );

        const linhasPorChave = new Map();
        porCotacao.forEach(({ id, linhas }) => {
          linhas.forEach((linha) => {
            const chave = `${linha.campouf}|${linha.campodt}`;
            if (!linhasPorChave.has(chave)) {
              linhasPorChave.set(chave, {
                key: chave,
                campouf: linha.campouf,
                campodtOriginal: linha.campodt,
              });
            }
            const registro = linhasPorChave.get(chave);
            registro[`valor_${id}`] = linha.valor;
            registro[`dolar_${id}`] = linha.valorDolar;
            // Cotação do dólar do dia (a mesma para todas as cotações da
            // linha, que é por UF+Data) — usada na média em dólar.
            if (linha.dolarRate) registro.dolarRate = linha.dolarRate;
          });
        });

        const idsCotacoes = queries.map(({ id }) => id);

        const linhasFinais = Array.from(linhasPorChave.values())
          .sort((a, b) => {
            if (a.campouf !== b.campouf) {
              return a.campouf.localeCompare(b.campouf);
            }
            return new Date(b.campodtOriginal) - new Date(a.campodtOriginal);
          })
          .map((registro) => {
            // Média só dos valores (não do dólar) das cotações selecionadas
            // que realmente vieram pra essa linha (UF+Data) — ignora as que
            // ficaram em branco em vez de tratar como zero.
            const valoresPresentes = idsCotacoes
              .map((id) => registro[`valor_${id}`])
              .filter((v) => v !== undefined && v !== null);
            const valorMedio = valoresPresentes.length
              ? Number(
                  valoresPresentes.reduce((soma, v) => soma + Number(v), 0) /
                    valoresPresentes.length
                ).toFixed(2)
              : null;

            // Média em dólar: a média em reais convertida pelo dólar do dia.
            // Dá o mesmo que a média das colunas "Dólar" da linha (o dólar é
            // um só por data), mas sem somar valores já arredondados.
            const valorMedioDolar =
              valorMedio !== null && registro.dolarRate
                ? Number(Number(valorMedio) / registro.dolarRate).toFixed(2)
                : null;

            return {
              ...registro,
              campodt: formatDate(registro.campodtOriginal),
              valorMedio,
              valorMedioDolar,
            };
          });

        const colunasFinais = [
          { title: "UF", dataIndex: "campouf", fixed: "left", width: 90 },
          { title: "Data", dataIndex: "campodt", fixed: "left", width: 110 },
          {
            title: "Valor Médio",
            dataIndex: "valorMedio",
            fixed: "left",
            width: 120,
            render: (v) => v ?? "-",
          },
          {
            title: "Valor Médio (US$)",
            dataIndex: "valorMedioDolar",
            fixed: "left",
            width: 130,
            render: (v) => v ?? "-",
          },
          ...queries.flatMap(({ id, nome }) => [
            {
              title: `${nome} — Valor`,
              dataIndex: `valor_${id}`,
              render: (v) => (v ?? v === 0 ? v : "-"),
            },
            {
              title: `${nome} — Dólar`,
              dataIndex: `dolar_${id}`,
              render: (v) => v ?? "-",
            },
          ]),
        ];

        if (!cancelado) {
          setDadosTabela(linhasFinais);
          setColunas(colunasFinais);
          // Mesmas colunas da tela, para a página exportar em Excel.
          if (onDados) {
            onDados("mesclado", {
              nome: "Relatório Mesclado",
              titulo: `Relatório Mesclado — ${queries.map((q) => q.nome).join(", ")}`,
              colunas: colunasFinais.map((c) => ({
                titulo: c.title,
                chave: c.dataIndex,
                tipo: c.dataIndex === "campouf" ? undefined : c.dataIndex === "campodt" ? "data" : "numero",
              })),
              linhas: linhasFinais,
            });
          }
        }
      } catch (error) {
        console.error("Erro ao mesclar cotações:", error);
        toast.error("Ocorreu um erro inesperado ao mesclar as cotações!");
      } finally {
        if (!cancelado) setIsLoading(false);
      }
    }

    montar();
    return () => {
      cancelado = true;
    };
  }, [queries, allUfs, onDados]);

  return (
    <Content
      style={{
        padding: 10,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        zIndex: 0,
      }}
      className="slide-right"
    >
      <Descriptions
        title={"Relatório Mesclado"}
        column={1}
        items={[
          {
            key: "1",
            label: "Cotações",
            children: (queries || []).map((q) => q.nome).join(", "),
          },
        ]}
      />
      <Table
        loading={isLoading}
        columns={colunas}
        dataSource={dadosTabela}
        pagination={false}
        scroll={{ x: "max-content" }}
      />
    </Content>
  );
}
